import * as d3 from "d3";
import moment from "moment";

class CreateTimeWheelChart {
  svg;
  parent;
  svgG;
  arcG;
  emptyArcG;
  chartData = [];
  mappedChartData = [];
  userData;
  timeArcG;
  updateDataCB;
  width;
  height;
  margin = 50;
  padding = { left: 3, right: 2 };
  pieInnerRadius;
  pieOutterRadius;
  pie = d3
    .pie()
    .value((d) => d.degrees)
    .sort((a, b) => {
      return a.start_time - b.start_time;
    });
  arcGenerator;
  currentSegment;
  isViewMode;
  hiddenArcs;
  hiddenArcSvg;
  polyLinesG;
  titleG;
  getSelectedArcs;
  selectedArcs = [];
  maxSelectedArcs = 4;
  arcHoverEvents;
  isShowLabelOn = false;
  buttonG;
  arcThickness = 0;
  handleProfileClick;
  config = {
    shouldShowButtons: false,
  };

  constructor(
    element,
    data,
    userData,
    updateDataCB,
    isViewMode,
    getSelectedArcs,
    maxSelectedArcs,
    arcHover,
    arcThickness,
    handleProfileClick,
    config
  ) {
    this.updateDataCB = updateDataCB;
    this.parent = element;
    d3.select(this.parent).selectAll("svg").remove();
    this.svg = d3.select(element).append("svg");
    this.chartData = data ?? [];
    this.userData = userData;
    this.isViewMode = isViewMode;
    this.getSelectedArcs = getSelectedArcs ? getSelectedArcs : () => {};
    this.maxSelectedArcs = maxSelectedArcs;
    this.arcHoverEvents = !!arcHover ? arcHover : () => {};
    if (arcThickness) {
      this.arcThickness = arcThickness;
    }
    this.handleProfileClick = !!handleProfileClick
      ? handleProfileClick
      : () => {};
    this.setSvg();
    this.config = config;
  }

  resize() {
    this.width = this.parent.clientWidth - this.margin;
    this.height = this.parent.clientHeight - this.margin;
    this.svg
      .attr("width", this.width + this.margin)
      .attr("height", this.height + this.margin);
    this.pieInnerRadius = Math.min(this.width, this.height) / this.padding.left;
    this.pieOutterRadius =
      Math.min(this.width, this.height) / this.padding.right;
    this.arcGenerator = d3
      .arc()
      .innerRadius(this.pieInnerRadius + this.padding.left + this.padding.right)
      .outerRadius((d, i) => {
        return (
          this.pieOutterRadius +
          this.arcThickness +
          (d?.data?.selected ? 10 : 0)
        );
      });
    this.emptyWheelArcGenerator = d3
      .arc()
      .innerRadius(this.pieInnerRadius + this.padding.left + this.padding.right)
      .outerRadius((d, i) => {
        return (
          this.pieOutterRadius - (this.padding.left + this.padding.right) * 2
        );
      });
  }

  setMode(isViewMode) {
    this.isViewMode = isViewMode;
  }

  setSvg() {
    this.svg.selectAll("g").remove();
    this.resize();
    this.svgG = this.svg
      .append("g")
      .attr("class", "svg-g")
      .attr(
        "transform",
        `translate(${(this.width + this.margin) / this.padding.right},${
          (this.height + this.margin) / this.padding.right
        })`
      );

    this.titleG = this.svgG
      .append("g")
      .attr("class", "title-text")
      .attr("font-family", "sans-serif")
      .attr("font-size", 12)
      .attr("text-anchor", "middle");

    this.polyLinesG = this.svgG.append("g").attr("class", "poly-lines");

    this.emptyArcG = this.svgG.append("g").attr("class", "empty-arc");
    this.timeArcG = this.svgG.append("g").attr("class", "time-arc");
    this.hiddenArcSvg = this.svg
      .append("g")
      .attr("class", "hidden-g")
      .attr(
        "transform",
        `translate(${(this.width + this.margin) / this.padding.right},${
          (this.height + this.margin) / this.padding.right
        })`
      );

    this.hiddenArcs = this.hiddenArcSvg.append("g").attr("class", "hidden-arc");
    this.buttonG = this.svgG.append("g").attr("class", "button-g");
    this.setProfile(this.userData);
    this.setEmptyView();
  }

  setProfile(userData) {
    this.userData = userData;
    this.svgG.selectAll(".profile").remove();
    const center = this.svgG.append("g").attr("class", "profile");

    const patterns = this.svgG
      .selectAll("defs.patterns")
      .data([this.userData])
      .enter()
      .append("svg:defs")
      .attr("class", "patterns")
      .selectAll("pattern")
      .data(function (d, i) {
        return [d];
      })
      .enter()
      .append("svg:pattern")
      .attr("class", "pattern")
      .attr("id", function (d, i) {
        return d.id;
      })
      .attr("x", "0%")
      .attr("y", "0%")
      .attr("height", "100%")
      .attr("width", "100%");
    const image = patterns
      .selectAll(".pattern")
      .data(function (d, i) {
        return [d];
      })
      .enter()
      .append("svg:image")
      .attr("x", "0%")
      .attr("y", "0%")
      .attr("height", this.pieInnerRadius * 2)
      .attr("width", this.pieInnerRadius * 2)
      .attr("xlink:href", (d) => d.url)
      .attr("preserveAspectRatio", "xMidYMid slice");

    const centerSvg = center.append("g");

    const handleClick = () => {
      this.handleProfileClick();
    };

    centerSvg
      .append("circle")
      .attr("r", this.pieInnerRadius)
      .style("fill", "url(#" + this.userData.id + ")")
      .on("click", handleClick)
      .style("cursor", "pointer");

    centerSvg
      .append("circle")
      .attr("r", this.pieInnerRadius - 2)
      .style("fill", "none")
      .style("stroke", "white")
      .style("stroke-width", "5px")
      .style("opacity", 0.5);
  }

  setEmptyView() {
    const startTime = "00:00:00";
    const endTime = "23:59:00";
    const getEmptyWheelArray = [];
    this.divideDurationIntoIntervals(startTime, endTime, getEmptyWheelArray);
    getEmptyWheelArray.map((d) => {
      d.id = d.start_time.replace(/:/g, "");
      d.color = "#F6F7F9";
      d.degrees = 15;
      return d;
    });
    const arcs = this.emptyArcG
      .selectAll(".empty-arc")
      .data(this.pie(getEmptyWheelArray), (d) => d.data.id);
    arcs.exit().remove();
    arcs
      .enter()
      .append("path")
      .attr("class", "empty-arc")
      .attr("d", this.emptyWheelArcGenerator)
      .attr("fill", (d) => d.data.color)
      .on("click", this.createArc.bind(this))
      .style("cursor", "pointer");
  }

  manageLabels(event) {
    this.isShowLabelOn = event;
    if (this.isShowLabelOn) {
      this.updatedTitle();
    } else {
      this.titleG.selectAll("text").remove();
      this.polyLinesG.selectAll("polyline").remove();
    }
  }

  updatedTitle() {
    const isDoubleCol = this.chartData.length > 20;
    const extraSpacing = isDoubleCol ? 30 : 0;
    const self = this;
    const arcLabel = d3
      .arc()
      .innerRadius(this.pieOutterRadius * 1)
      .outerRadius(this.pieOutterRadius * 1.2);

    const midAngle = (d) => d.startAngle + (d.endAngle - d.startAngle) / 2;

    const label_position = (d, i) => {
      let pos = arcLabel.centroid(d);
      pos[0] =
        self.pieOutterRadius * (midAngle(d) < Math.PI ? 1 : -1) +
        (midAngle(d) < Math.PI
          ? self.margin / 2 + self.padding.left
          : -(self.margin / 2) - self.padding.left);
      if (isDoubleCol && (i || i == 0)) {
        if (i % 2 == 0) {
          pos[0] = pos[0] - extraSpacing + (midAngle(d) < Math.PI ? 0 : -35);
        } else {
          pos[0] = pos[0] + extraSpacing + (midAngle(d) < Math.PI ? 35 : 0);
        }
      }
      return pos;
    };

    const label_anchor = (d) => {
      return midAngle(d) < Math.PI ? "start" : "end";
    };

    const arcLine = d3
      .arc()
      .innerRadius(this.pieOutterRadius)
      .outerRadius(this.pieOutterRadius * 0.8);

    const line_position = (d, i) => {
      let arcLabels = arcLabel.centroid(d);
      let pos = [];
      pos[0] =
        self.pieOutterRadius * 1 * (midAngle(d) < Math.PI ? 1 : -1) +
        (midAngle(d) < Math.PI ? self.margin / 2 : -(self.margin / 2));
      pos[1] = arcLabels[1];
      if (isDoubleCol && (i || i == 0)) {
        if (i % 2 == 0) {
          pos[0] = pos[0] - extraSpacing + (midAngle(d) < Math.PI ? 0 : -35);
        } else {
          pos[0] = pos[0] + extraSpacing + (midAngle(d) < Math.PI ? 35 : 0);
        }
      }
      return [arcLine.centroid(d), arcLabels, pos];
    };
    this.titleG.exit().remove();
    this.polyLinesG.exit().remove();

    const spliceString = (string) => {
      if (string?.length <= 15) {
        return string;
      } else {
        return string?.substring(0, 15 - 3) + "...";
      }
    };
    this.titleG
      .selectAll("text")
      .data(this.pie(this.mappedChartData), (d) => d.data.id)
      .join((enter) =>
        enter.append("text").call((selection) =>
          selection
            .attr("dy", ".35em")
            .attr("font-size", "12px")
            .attr("transform", (d, i) => `translate(${label_position(d, i)})`)
            .style("text-anchor", (d) => label_anchor(d))
            .call((text) =>
              text
                .append("tspan")
                .text((d) => (d.data.isEmpty ? "" : spliceString(d.data.title)))
            )
        )
      );

    this.polyLinesG
      .selectAll("polyline")
      .data(this.pie(this.mappedChartData), (d) => d.data.id)
      .join((enter) =>
        enter
          .append("polyline")
          .style("fill", "none")
          .style("opacity", 0.5)
          .style("stroke", (d) => d.data.color)
          .style("stroke-width", "1px")
          .call((selection) =>
            selection.attr("points", (d, i) => {
              return d.data.isEmpty ? 0 : line_position(d, i);
            })
          )
      );
  }

  getMinuteDuration = (startTime, endTime) => {
    const startMoment = d3.timeParse("%H:%M:%S")(startTime);
    const endMoment = d3.timeParse("%H:%M:%S")(endTime);
    return d3.timeMinute.count(startMoment, endMoment);
  };
  colors = [
    "#badbc3",
    "#e6bad5",
    "#e6e3ba",
    "#b5bee8",
    "#fa6b73",
    "#82e0d1",
    "##e0c482",
    "#70db48",
    "#3c13d1",
    "#e0d755",
  ];

  createArc(event, i) {
    delete i?.data?.isEmpty;

    const randomColor =
      this.colors[Math.floor(Math.random() * this.colors.length)];
    const index = this.colors.findIndex((d) => randomColor);
    this.colors.slice(index, 1);
    i.data.color = randomColor;
    if (!this.chartData?.length) {
      this.chartData = [];
    }
    this.chartData.push({ ...i.data });
    this.updateDataCB([...this.chartData]);
  }

  mapTimeWheelData(data) {
    let sortedData = data.sort((a, b) =>
      moment(a.start_time, "HH:mm:ss").diff(moment(b.start_time, "HH:mm:ss"))
    );

    let lastValue;
    const emptyArcs = [];
    const startTime = "00:00:00";
    const endTime = "23:59:00";

    const checkAndPushData = (startTime, endTime) => {
      const duration = this.getMinuteDuration(startTime, endTime);
      if (duration >= 15 || endTime == "23:59:00") {
        const emptyArc = {
          start_time: startTime,
          end_time: endTime,
        };
        emptyArcs.push(emptyArc);
      }
    };
    sortedData.map((d, i) => {
      if (i == 0) {
        checkAndPushData(startTime, d.start_time);

        //Manage interval when there is only one arc
        if (sortedData.length == 1) {
          checkAndPushData(d.end_time, endTime);
        }
      } else if (i == data.length - 1) {
        //manage interval between last arc & last time
        checkAndPushData(d.end_time, endTime);

        // To manage interval between two arcs
        checkAndPushData(lastValue.end_time, d.start_time);
      } else {
        //to manage other intervals
        checkAndPushData(lastValue.end_time, d.start_time);
      }
      lastValue = d;
      return d;
    });
    let createHourlyArcs = [];
    emptyArcs.forEach((d) => {
      this.divideDurationIntoIntervals(
        d.start_time,
        d.end_time,
        createHourlyArcs
      );
    });
    createHourlyArcs.map((d) => {
      d.id = d.start_time.replace(/:/g, "") + Math.random(10000);
      d.isEmpty = true;
      d.color = "transparent";
      return d;
    });
    sortedData = [...sortedData, ...createHourlyArcs];
    const degreesPerMinute = 360 / (24 * 60);

    sortedData.map((d) => {
      const durationInMinutes = this.getMinuteDuration(
        d.start_time,
        d.end_time
      );
      d.degrees = durationInMinutes * degreesPerMinute;

      d.value = this.getDuration(d.start_time, d.end_time);
      //IP_906
      // d.value = moment(d.end_time, "HH:mm:ss").diff(
      //   moment(d.start_time, "HH:mm:ss"),
      //   "minutes"
      // );
      return d;
    });
    sortedData = sortedData.sort((a, b) =>
      moment(a.start_time, "HH:mm:ss").diff(moment(b.start_time, "HH:mm:ss"))
    );
    return sortedData;
  }
  //IP-953 - done
  updateArcs(data, isArcSizeIncreased) {
    this.chartData = data ?? [];
    if (!data.length) {
      return;
    }
    const sortedData = this.mapTimeWheelData(data);
    this.mappedChartData = [...sortedData];

    this.resize();
    const arcs = this.timeArcG
      .selectAll(".single-arc")
      .data(this.pie(sortedData), (d) => d.data.id);
    arcs.exit().remove();
    arcs
      .enter()
      .append("path")
      .attr("class", "single-arc")
      .attr("d", this.arcGenerator)
      .attr("fill", (d) => d.data.color)
      .style("cursor", "pointer")
      .style("stroke-width", 1)
      .style("stroke", (d) => (d.data.isEmpty ? "transparent" : "white"))
      .merge(arcs)
      .transition()
      .duration(isArcSizeIncreased ? 0 : 200)
      .ease(d3.easeLinear)
      .attr("class", "single-arc")
      .style("stroke", (d) => (d.data.isEmpty ? "transparent" : "white"))
      .attr("fill", (d) => d.data.color)

      .attr("d", this.arcGenerator);
    this.mapDndChartData();
    if (this.isShowLabelOn) {
      this.titleG.selectAll("text").remove();
      this.polyLinesG.selectAll("polyline").remove();
      this.updatedTitle();
    }
  }

  isHour(time) {
    const timeParts = time.split(":");
    const minutes = parseInt(timeParts[1]);
    const seconds = parseInt(timeParts[2]);

    return minutes === 0 && seconds === 0;
  }

  divideDurationIntoIntervals(startTime, endTime, resultArray) {
    if (!startTime || !endTime) {
      return [];
    }

    const pushTimeSet = (start, end) => {
      resultArray.push({
        start_time: start,
        end_time: end,
      });
    };

    const durationInMinutes = this.getDuration(startTime, endTime);

    if (durationInMinutes >= 60) {
      const nextHourTime = this.getNextHour(startTime);
      pushTimeSet(startTime, nextHourTime.format("HH:mm:ss"));
      // resultArray.push({
      //   start_time: startTime,
      //   end_time: nextHourTime.format("HH:mm:ss"),
      // });
      if (
        !this.isHour(startTime) ||
        nextHourTime.format("HH:mm:ss") != endTime
      ) {
        return this.divideDurationIntoIntervals(
          nextHourTime.format("HH:mm:ss"),
          endTime,
          resultArray
        );
      }
    } else if (durationInMinutes > 0) {
      const startNextHourTime = this.getNextHour(startTime);
      const EndNextHourTime = this.getNextHour(startTime);

      if (
        startNextHourTime.format("HH:mm:ss") != endTime &&
        EndNextHourTime.format("HH:mm:ss") !=
          startNextHourTime.format("HH:mm:ss")
      ) {
        pushTimeSet(startTime, startNextHourTime.format("HH:mm:ss"));

        pushTimeSet(startNextHourTime.format("HH:mm:ss"), endTime);
      } else if (
        EndNextHourTime.format("HH:mm:ss") ==
        startNextHourTime.format("HH:mm:ss")
      ) {
        pushTimeSet(startTime, endTime);
      }
    }

    return resultArray;
  }

  getNextHour(startTime) {
    if (this.isHour(startTime)) {
      return moment(startTime, "HH:mm:ss").add(60, "minutes");
    } else {
      const startTimeParts = startTime.split(":");
      let hour = parseInt(startTimeParts[0]);
      let minute = parseInt(startTimeParts[1]);

      if (minute >= 15) {
        hour += 1;
      }

      if (hour === 24) {
        hour = 0;
      }

      const nextHour = hour.toString().padStart(2, "0");
      return moment(`${nextHour}:00:00`, "HH:mm:ss");
    }
  }

  mapDndChartData() {
    this.setHiddenArc(this.mappedChartData);
  }

  findAdjacentNonEmptyIndices(arr, index, data) {
    const resultIndices = new Set();
    resultIndices.add(index);
    for (let i = index - 1; i >= 0; i--) {
      if ((!arr[i] || !arr[i].isEmpty) && arr[i]?.id != data?.id) {
        break;
      }
      resultIndices.add(i);
    }

    for (let i = index + 1; i < arr.length; i++) {
      if ((!arr[i] || !arr[i].isEmpty) && arr[i]?.id != data?.id) {
        break;
      }
      resultIndices.add(i);
    }
    return Array.from(resultIndices);
  }

  // adjustDurationForObjectWithSections(data, index, duration) {
  //   const adjacentIndices = this.findAdjacentNonEmptyIndices(data, index);
  //   const totalAdjacentDuration = adjacentIndices.reduce(
  //     (total, i) => total + data[i].duration,
  //     0
  //   );

  //   if (totalAdjacentDuration >= duration) {
  //     const currentSectionDuration = data[index].duration;
  //     const remainingDuration = duration - currentSectionDuration;

  //     const numberOfAdjacentSections = adjacentIndices.length;
  //     const adjustedDurationForEach =
  //       remainingDuration / numberOfAdjacentSections;

  //     data[index].duration += adjustedDurationForEach;
  //     adjacentIndices.forEach((adjacentIndex) => {
  //       data[adjacentIndex].duration += adjustedDurationForEach;
  //     });
  //   } else {
  //     const adjustedDurationForEach = duration / (adjacentIndices.length + 1);

  //     data[index].duration = adjustedDurationForEach;
  //     adjacentIndices.forEach((adjacentIndex) => {
  //       data[adjacentIndex].duration = adjustedDurationForEach;
  //     });
  //   }
  // }

  getDuration(startTime, endTime) {
    const startMoment = moment(startTime, "HH:mm:ss");
    const endMoment = moment(endTime, "HH:mm:ss");
    return moment.duration(endMoment.diff(startMoment)).asMinutes() ?? 0;
  }

  mapExtraSpace(requiredDuration, prevIndex, nextIndex) {
    const indexData = [];
    let prevDuration = 0;
    let nextDuration = 0;
    if (prevIndex >= 0) {
      prevDuration = this.getDuration(
        this.mappedChartData[prevIndex].start_time,
        this.mappedChartData[prevIndex].end_time
      );
    }
    if (nextIndex < this.mappedChartData.length) {
      nextDuration =
        this.getDuration(
          this.mappedChartData[nextIndex].start_time,
          this.mappedChartData[nextIndex].end_time
        ) ?? 0;
    }
    let takenFromPrev = 0;
    let takenFromNext = 0;
    if (
      prevDuration >= requiredDuration / 2 &&
      nextDuration >= requiredDuration / 2
    ) {
      if (requiredDuration < prevDuration) {
        takenFromPrev = requiredDuration;
        takenFromNext = 0;
      } else if (requiredDuration < nextDuration) {
        takenFromPrev = 0;
        takenFromNext = requiredDuration;
      } else {
        takenFromPrev = Math.round(prevDuration / 15) * 15;
        if (requiredDuration - takenFromPrev != 0) {
          takenFromNext = Math.round(
            ((requiredDuration - takenFromPrev) / 15) * 15
          );
        }
      }
    } else if (prevDuration < requiredDuration) {
      takenFromPrev = prevDuration;
      takenFromNext = requiredDuration - prevDuration;
    } else if (nextDuration < requiredDuration) {
      takenFromPrev = requiredDuration - nextDuration;
      takenFromNext = nextDuration;
    }
    if (prevIndex >= 0 && takenFromPrev != 0) {
      indexData.push({ index: prevIndex, value: takenFromPrev });
    }

    if (nextIndex < this.mappedChartData.length && takenFromNext != 0) {
      indexData.push({ index: nextIndex, value: takenFromNext });
    }
    return indexData;
  }

  mapSegmentInEmptyArc(EmptyArcIndex, data, currentIndex) {
    const totalAdjacentDuration = EmptyArcIndex.reduce((total, i) => {
      const duration = this.getDuration(
        this.mappedChartData[i]?.start_time,
        this.mappedChartData[i]?.end_time
      );
      total = total + duration;
      return total;
    }, 0);
    const currentDuration = data.value;
    //IP_906
    //const currentDuration = this.getDuration(data.start_time, data.end_time);
    EmptyArcIndex = EmptyArcIndex.sort((a, b) => a - b);

    if (isNaN(totalAdjacentDuration) || isNaN(currentDuration)) {
      return;
    }

    const getSumData = (prevIndex, nextIndex) => {
      let prevDuration, nextDuration;
      if (prevIndex) {
        prevDuration = this.getDuration(
          this.mappedChartData[prevIndex]?.start_time,
          this.mappedChartData[nextIndex ?? prevIndex]?.end_time
        );
      }
      if (nextIndex) {
        nextDuration = this.getDuration(
          this.mappedChartData[prevIndex ?? nextIndex]?.start_time,
          this.mappedChartData[nextIndex]?.end_time
        );
      }

      return {
        total: prevDuration ?? 0 + nextDuration ?? 0,
        prev: prevDuration,
        next: nextDuration,
      };
    };

    const getRangeFromArray = (dataArray) => {
      if (!dataArray.length) {
        return;
      }
      const startTime = this.mappedChartData[dataArray[0].index];
      const EndTime =
        this.mappedChartData[dataArray[dataArray.length - 1].index];
      const startDuration = this.getDuration(
        startTime.start_time,
        startTime.end_time
      );
      const EndDuration = this.getDuration(
        EndTime?.start_time,
        EndTime?.end_time
      );
      let range = {
        start_time: startTime?.start_time,
        end_time: EndTime?.end_time ?? "23:59:00",
      };
      if (startDuration != dataArray[0].value) {
        range.start_time = moment(startTime?.end_time, "HH:mm:ss")
          .subtract(dataArray[0].value, "minutes")
          .format("HH:mm:ss");
      }
      if (EndDuration && EndDuration != dataArray[dataArray.length - 1].value) {
        range.end_time = moment(EndTime?.start_time, "HH:mm:ss")
          .add(dataArray[dataArray.length - 1].value, "minutes")
          .format("HH:mm:ss");
      }
      return range;
    };
    if (totalAdjacentDuration <= currentDuration) {
      const startIndex = EmptyArcIndex[0];
      const EndIndex = EmptyArcIndex[EmptyArcIndex.length - 1];
      if (startIndex)
        return {
          start_time: this.mappedChartData[startIndex].start_time,
          end_time: this.mappedChartData[EndIndex].end_time,
        };
    } else {
      const currentI = EmptyArcIndex.findIndex((d) => d == currentIndex);
      const droppedArc = this.mappedChartData[currentIndex];
      const droppedArcDuration = this.getDuration(
        droppedArc.start_time,
        droppedArc.end_time
      );
      let requiredDuration = currentDuration - droppedArcDuration;
      let indexToCover = [];
      indexToCover.push({ index: currentIndex, value: droppedArcDuration });
      const length = Math.ceil(EmptyArcIndex.length / 2);
      for (let i = 1; i <= length; i++) {
        const nearValueSum = getSumData(
          EmptyArcIndex[currentI - i],
          EmptyArcIndex[currentI + i]
        );
        if (requiredDuration >= nearValueSum.total) {
          indexToCover.push({
            index: EmptyArcIndex[currentI - i],
            value: nearValueSum.prev,
          });
          indexToCover.push({
            index: EmptyArcIndex[currentI + i],
            value: nearValueSum.next,
          });
          requiredDuration = requiredDuration - nearValueSum.total;
          if (requiredDuration == 0) {
            break;
          }
        } else {
          const indexData = this.mapExtraSpace(
            requiredDuration,
            EmptyArcIndex[currentI - i],
            EmptyArcIndex[currentI + i],
            EmptyArcIndex
          );
          indexToCover = [...indexToCover, ...indexData];
          break;
        }
      }
      indexToCover = indexToCover.sort((a, b) => a.index - b.index);
      return getRangeFromArray(indexToCover);
    }
  }

  dragstart(d, index) {
    this.draggedElement = { ...index.data };
  }

  drag(d, index) {
    if (this.isViewMode || index.data.isEmpty) {
      return;
    }
    this.timeArcG.selectAll("path").attr("opacity", (dd, ii) => {
      return index.data?.id == dd.data?.id ? 1 : 0.3;
    });
    //IP_906
    index.data.value = this.draggedElement.value;

    if (
      this.currentSegment !== null &&
      index.data.id != this.currentSegment.id &&
      this.currentSegment.isEmpty
    ) {
      const newDuration = this.getDuration(
        this.currentSegment.start_time,
        this.currentSegment.end_time
      );
      const startMoment = moment(this.currentSegment.start_time, "HH:mm:ss");
      const currentIndex = this.mappedChartData.findIndex(
        (d) => d.id == this.currentSegment.id
      );
      const oldIndex = this.mappedChartData.findIndex(
        (d) => d.id == index.data.id
      );
      let mapData = [...this.chartData].map((d) => {
        if (d.id == index.data.id) {
          const oldDuration = d.value;
          //IP_906
          //const oldDuration = this.getDuration(d.start_time, d.end_time);

          if (
            newDuration >= oldDuration &&
            (currentIndex + 1 != oldIndex || currentIndex - 1 != oldIndex)
          ) {
            d.start_time = startMoment.format("HH:mm:ss");
            d.end_time = moment(startMoment)
              .add(oldDuration, "minutes")
              .format("HH:mm:ss");
          } else {
            const allNonEmpty = this.findAdjacentNonEmptyIndices(
              [...this.mappedChartData],
              currentIndex,
              index.data
            );
            if (allNonEmpty.length) {
              const getTimeRange = this.mapSegmentInEmptyArc(
                [...allNonEmpty],
                {
                  ...index.data,
                },
                currentIndex
              );
              if (getTimeRange) {
                const timeParts = getTimeRange?.start_time.split(":");
                const minutes = timeParts[1];
                if (
                  getTimeRange.end_time == "23:59:00" &&
                  minutes.includes("9")
                ) {
                  getTimeRange.start_time = this.getNextHour(
                    getTimeRange?.start_time
                  ).format("HH:mm:ss");
                }
                d.start_time = getTimeRange?.start_time;
                d.end_time = getTimeRange?.end_time;
              }
            } else {
              d.start_time = startMoment.format("HH:mm:ss");
              d.end_time = moment(startMoment)
                .add(newDuration, "minutes")
                .format("HH:mm:ss");
            }
          }
        }
        return d;
      });

      mapData = mapData.sort((a, b) => {
        return a.start_time - b.start_time;
      });
      this.updateDataCB(mapData);
    }
  }

  dragEnd(d, i) {
    this.timeArcG.selectAll("path").attr("opacity", 1);
    //IP_906
    this.draggedElement = null;
  }

  arcSelected(event, d) {
    const updatedData = this.chartData.map((arcData) => {
      if (arcData.id === d.data.id) {
        arcData.selected = !arcData.selected;
        if (arcData.selected) {
          this.selectedArcs.push(arcData.id);
          if (this.selectedArcs.length > this.maxSelectedArcs) {
            const selectedId = this.selectedArcs[0];
            this.chartData.map((d) => {
              if (d.id == selectedId) {
                d.selected = false;
              }
            });
            this.selectedArcs.splice(0, 1);
          }
        } else {
          const index = this.selectedArcs.findIndex((ar) => ar == d.data.id);
          this.selectedArcs.splice(index, 1);
        }
      }
      return arcData;
    });

    this.setSelectedArcSides();
    this.updateDataCB(updatedData);
  }

  pie_in(d, i) {
    this.currentSegment = i?.data;
    const hoveredData = this.chartData.find(
      (data) => data.id == this.currentSegment.id
    );
    if (!i.data.isEmpty) {
      // this.showPlusMinusButton(i);
    }
    this.arcHoverEvents(hoveredData);
  }

  pie_out(d, i) {
    this.currentSegment = null;
    // this.hidePlusMinusButton(i);
    this.arcHoverEvents(null);
  }

  setHiddenArc(mappedData) {
    this.hiddenArcs.exit().remove();
    this.hiddenArcs
      .selectAll("path")
      .data(this.pie([...mappedData]), (d) => d.data.id)
      .join(
        (enter) =>
          enter.append("path").call((selection) => {
            selection
              .call(
                d3
                  .drag()
                  .on("start", this.dragstart.bind(this))
                  .on("drag", this.drag.bind(this))
                  .on("end", this.dragEnd.bind(this))
              )
              .style("cursor", "pointer")
              .attr("fill", "transparent")
              .attr("id", (d) => d.data.id)
              .attr("d", this.emptyWheelArcGenerator)
              .on("mouseover", this.pie_in.bind(this))
              .on("mouseout", this.pie_out.bind(this))
              .on("click", (d, i) => {
                if (i.data.isEmpty) {
                  this.createArc.bind(this)(d, i);
                } else {
                  this.arcSelected.bind(this)(d, i);
                }
              });
          }),
        (update) =>
          update.call((selection) => {
            selection
              .attr("d", this.emptyWheelArcGenerator)
              .style("cursor", (d) => (d.data.isEmpty ? "pointer" : "move"));
          })
      );
  }

  setSelectedArcSides() {
    let arcsData = [];
    const arcsAngles = this.pie(this.chartData);
    this.chartData.forEach((element) => {
      if (element.selected) {
        const arc = { id: element.id, side: "" };
        const arcAngle = arcsAngles.find((d) => d.data.id == element.id);
        if (arcAngle) {
          const midpointAngle = (arcAngle.startAngle + arcAngle.endAngle) / 2;
          if (midpointAngle == Math.PI) {
            arc.side = "left"; //was middle before
          } else if (midpointAngle > Math.PI) {
            arc.side = "left";
          } else {
            arc.side = "right";
          }
        }
        arcsData.push({ ...arc, ...element, arcAngle });
      }
    });
    this.getSelectedArcs(arcsData);
  }

  // showPlusMinusButton(event) {
  //   if (!this.config.shouldShowButtons) {
  //     return;
  //   }
  //   this.buttonG.selectAll("g").remove();

  //   const currentArcDuration = this.getDuration(
  //     event.data.start_time,
  //     event.data.end_time
  //   );

  //   const startAngle = event.startAngle;
  //   const endAngle = event.endAngle;
  //   const startX = this.pieOutterRadius * Math.cos(startAngle - Math.PI / 2);
  //   const startY = this.pieOutterRadius * Math.sin(startAngle - Math.PI / 2);

  //   const endX = this.pieOutterRadius * Math.cos(endAngle - Math.PI / 2);
  //   const endY = this.pieOutterRadius * Math.sin(endAngle - Math.PI / 2);

  //   const buttonsForStart = [
  //     {
  //       label: "-",
  //       button: "minus",
  //     },
  //     {
  //       label: "+",
  //       button: "plus",
  //     },
  //   ];

  //   const buttonsForEnd = [
  //     {
  //       label: "+",
  //       button: "plus",
  //     },
  //     {
  //       label: "-",
  //       button: "minus",
  //     },
  //   ];

  //   const setButtonsVisibility = (data, isStartAngel) => {
  //     if (data.button == "minus" && currentArcDuration <= 15) {
  //       return "none";
  //     }

  //     if (
  //       data.button == "plus" &&
  //       this.checkForPrevNextNonEmptyArcs(event, isStartAngel)
  //     ) {
  //       return "none";
  //     }
  //     return "visible";
  //   };

  //   let startIconX = startX;
  //   let startIconY = startY;

  //   const buttonWidth = 20;
  //   const buttonHeight = 20;
  //   const cornerRadius = 10;

  //   const startAngleG = this.buttonG
  //     .append("g")
  //     .attr("class", "start-angel-g")
  //     .attr("transform", (d, i) => {
  //       return `translate(${startIconX}, ${startIconY})`;
  //     });

  //   const startMidpointAngle = (startAngle * 180) / Math.PI;

  //   const startRotationAngle = startMidpointAngle - 180;

  //   const startChildG = startAngleG
  //     .append("g")
  //     .attr("transform", `rotate(${startRotationAngle})`);
  //   const midpointAngle = (startAngle + endAngle) / 2;

  //   const buttonElements = startChildG
  //     .selectAll(".button")
  //     .data(buttonsForStart)
  //     .enter()
  //     .append("g")
  //     .attr("class", "button")
  //     .style("display", (d, i) => setButtonsVisibility(d, true))
  //     .on("click", (d, i) => this.onButtonClick(event, i, true))
  //     .attr("transform", (d, i) => {
  //       return `translate(${
  //         midpointAngle > Math.PI ? (i == 0 ? -10 : 10) : i == 0 ? -10 : 10
  //       }, 0)`;
  //     });

  //   buttonElements
  //     .append("path")
  //     .attr("d", (d) => {
  //       let isOneButton;
  //       if (d.button == "plus") {
  //         isOneButton = setButtonsVisibility({ button: "minus" }, true);
  //       } else {
  //         isOneButton = setButtonsVisibility({ button: "plus" }, true);
  //       }
  //       if (isOneButton == "none") {
  //         return this.bothRoundedRect(
  //           -buttonWidth / 2,
  //           -buttonHeight / 2,
  //           buttonWidth,
  //           buttonHeight,
  //           10
  //         );
  //       } else if (d.button == "plus") {
  //         return this.rightRoundedRect(
  //           -buttonWidth / 2,
  //           -buttonHeight / 2,
  //           buttonWidth,
  //           buttonHeight,
  //           cornerRadius
  //         );
  //       } else {
  //         return this.leftRoundedRect(
  //           -buttonWidth / 2,
  //           -buttonHeight / 2,
  //           buttonWidth,
  //           buttonHeight,
  //           10
  //         );
  //       }
  //     })
  //     .attr("fill", "#f0f1f2");

  //   buttonElements
  //     .append("text")
  //     .attr("text-anchor", "middle")
  //     .attr("font-size", "18px")
  //     .style("font-weight", "bold")
  //     .attr("fill", "blue")
  //     .attr("dy", ".35em")
  //     .text((d) => d.label);

  //   let EndIconX = endX;
  //   let endIconY = endY;
  //   const endAngleG = this.buttonG
  //     .append("g")
  //     .attr("class", "end-angel-g")
  //     .attr("transform", (d, i) => {
  //       return `translate(${EndIconX}, ${endIconY})`;
  //     });

  //   const midAngle2 = (startAngle + endAngle) / 2;
  //   const rotationAngle = (midAngle2 * 180) / Math.PI;
  //   const endChildG = endAngleG.append("g").attr("transform", (d, i) => {
  //     return `rotate(${rotationAngle})`;
  //   });

  //   const endButtonElements = endChildG
  //     .selectAll(".button")
  //     .data(buttonsForEnd)
  //     .enter()
  //     .append("g")
  //     .attr("class", "button")
  //     .style("display", (d, i) => setButtonsVisibility(d, false))
  //     .on("click", (d, i) => this.onButtonClick(event, i, false))
  //     .attr("transform", (d, i) => {
  //       return `translate(${
  //         midpointAngle > Math.PI ? (i == 0 ? 10 : -10) : i == 0 ? 10 : -10
  //       }, 0)`;
  //     });

  //   // Create a custom shape using a path
  //   endButtonElements
  //     .append("path")
  //     .attr("d", (d) => {
  //       let isOneButton;
  //       if (d.button == "plus") {
  //         isOneButton = setButtonsVisibility({ button: "minus" }, false);
  //       } else {
  //         isOneButton = setButtonsVisibility({ button: "plus" }, false);
  //       }
  //       if (isOneButton == "none") {
  //         return this.bothRoundedRect(
  //           -buttonWidth / 2,
  //           -buttonHeight / 2,
  //           buttonWidth,
  //           buttonHeight,
  //           10
  //         );
  //       } else if (d.button == "plus") {
  //         return this.rightRoundedRect(
  //           -buttonWidth / 2,
  //           -buttonHeight / 2,
  //           buttonWidth,
  //           buttonHeight,
  //           10
  //         );
  //       } else {
  //         return this.leftRoundedRect(
  //           -buttonWidth / 2,
  //           -buttonHeight / 2,
  //           buttonWidth,
  //           buttonHeight,
  //           cornerRadius
  //         );
  //       }
  //     })
  //     .attr("fill", "#f0f1f2");

  //   endButtonElements
  //     .append("text")
  //     .attr("text-anchor", "middle")
  //     .attr("font-size", "18px")
  //     .style("font-weight", "bold")
  //     .attr("fill", "blue")
  //     .attr("dy", ".35em")
  //     .text((d) => d.label);
  // }

  // rightRoundedRect(x, y, width, height, radius) {
  //   return (
  //     "M" +
  //     x +
  //     "," +
  //     y +
  //     "h" +
  //     (width - radius) +
  //     "a" +
  //     radius +
  //     "," +
  //     radius +
  //     " 0 0 1 " +
  //     radius +
  //     "," +
  //     radius +
  //     "v" +
  //     (height - 2 * radius) +
  //     "a" +
  //     radius +
  //     "," +
  //     radius +
  //     " 0 0 1 " +
  //     -radius +
  //     "," +
  //     radius +
  //     "h" +
  //     (radius - width) +
  //     "z"
  //   );
  // }

  // leftRoundedRect(x, y, width, height, radius) {
  //   return (
  //     "M" +
  //     (x + radius) +
  //     "," +
  //     y +
  //     "h" +
  //     (width - radius) +
  //     "v" +
  //     height +
  //     "h" +
  //     -(width - radius) +
  //     "a" +
  //     radius +
  //     "," +
  //     radius +
  //     " 0 0 1 " +
  //     -radius +
  //     "," +
  //     -radius +
  //     "v" +
  //     -(height - 2 * radius) +
  //     "a" +
  //     radius +
  //     "," +
  //     radius +
  //     " 0 0 1 " +
  //     radius +
  //     "," +
  //     -radius +
  //     "z"
  //   );
  // }

  // bothRoundedRect(x, y, width, height, radius) {
  //   return (
  //     "M" +
  //     (x + radius) +
  //     "," +
  //     y +
  //     "h" +
  //     (width - 2 * radius) +
  //     "a" +
  //     radius +
  //     "," +
  //     radius +
  //     " 0 0 1 " +
  //     radius +
  //     "," +
  //     radius +
  //     "v" +
  //     (height - 2 * radius) +
  //     "a" +
  //     radius +
  //     "," +
  //     radius +
  //     " 0 0 1 " +
  //     -radius +
  //     "," +
  //     radius +
  //     "h" +
  //     (2 * radius - width) +
  //     "a" +
  //     radius +
  //     "," +
  //     radius +
  //     " 0 0 1 " +
  //     -radius +
  //     "," +
  //     -radius +
  //     "v" +
  //     (2 * radius - height) +
  //     "a" +
  //     radius +
  //     "," +
  //     radius +
  //     " 0 0 1 " +
  //     radius +
  //     "," +
  //     -radius +
  //     "z"
  //   );
  // }
  // checkForPrevNextNonEmptyArcs(data, isStartAngel) {
  //   this.mappedChartData.sort((a, b) =>
  //     moment(a.start_time, "HH:mm:ss").diff(moment(b.start_time, "HH:mm:ss"))
  //   );
  //   if (
  //     (data.index == 0 && isStartAngel) ||
  //     (!isStartAngel && data.index == this.mappedChartData.length - 1)
  //   ) {
  //     return true;
  //   }
  //   if (isStartAngel) {
  //     return !this.mappedChartData[data.index - 1]?.isEmpty;
  //   } else {
  //     return !this.mappedChartData[data.index + 1]?.isEmpty;
  //   }
  // }

  // onButtonClick(data, event, isStartAngel) {
  //   const field = isStartAngel ? "start_time" : "end_time";
  //   this.chartData.sort((a, b) =>
  //     moment(a.start_time, "HH:mm:ss").diff(moment(b.start_time, "HH:mm:ss"))
  //   );
  //   if (
  //     (event.button == "plus" && !isStartAngel) ||
  //     (event.button == "minus" && isStartAngel)
  //   ) {
  //     const mapData = this.chartData.map((d) => {
  //       if (data.data.id == d.id) {
  //         d[field] = moment(d[field], "HH:mm:ss")
  //           .add(15, "minutes")
  //           .format("HH:mm:ss");
  //       }
  //       return d;
  //     });
  //     this.updateDataCB(mapData);
  //   } else if (
  //     (event.button == "minus" && !isStartAngel) ||
  //     (event.button == "plus" && isStartAngel)
  //   ) {
  //     const mapData = this.chartData.map((d) => {
  //       if (data.data.id == d.id) {
  //         d[field] = moment(d[field], "HH:mm:ss")
  //           .subtract(15, "minutes")
  //           .format("HH:mm:ss");
  //       }
  //       return d;
  //     });
  //     this.updateDataCB(mapData);
  //   }
  //   // this.showPlusMinusButton(data);
  // }

  // hidePlusMinusButton(event) {
  //   setTimeout(() => {
  //     this.buttonG.selectAll("g").remove();
  //   }, 6000);
  // }
}

export default CreateTimeWheelChart;